import logo from '@assets/logo.svg';
import { ErrorLayout } from '@components/templates';
import { Button } from '@components/ui/atoms/Button';
import { Flex } from '@components/ui/atoms/Flex';
import { Heading } from '@components/ui/atoms/Heading';
import { Image } from '@components/ui/atoms/Image';
import { Text } from '@components/ui/atoms/Text';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export type ErrorSecondaryAction = {
  label: string;
  onClick: () => void;
};

type Props = {
  bgImage: string;
  illustration: string;
  title: string;
  subtitle: string;
  description: string;
  errorCode: string;
  /** Optional secondary button shown next to "Go Back" (e.g. Reload Page, Contact Support) */
  secondaryAction?: ErrorSecondaryAction;
  /** When true, hides the default "Go Back" button. Use with primaryAction for login-only pages. */
  hideGoBack?: boolean;
  /** Custom primary action replacing the default "Go Back" (e.g. { label: 'Login', onClick: ... }) */
  primaryAction?: { label: string; onClick: () => void };
};

export const ErrorPage: React.FC<Props> = ({
  bgImage,
  illustration,
  title,
  subtitle,
  description,
  errorCode,
  secondaryAction,
  hideGoBack = false,
  primaryAction,
}) => {
  const navigate = useNavigate();
  const [isPrimaryLoading, setIsPrimaryLoading] = useState(false);

  const handleGoBack = () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    navigate(-1);
  };

  const handlePrimaryClick = useCallback(() => {
    if (!primaryAction) return;

    setIsPrimaryLoading(true);

    setTimeout(() => {
      primaryAction.onClick();
    }, 600);
  }, [primaryAction]);

  return (
    <ErrorLayout bgImage={bgImage} logo={logo}>
      <Flex h="full" alignItems="center" justifyContent="center" px={{ base: '24px', md: '0' }}>
        <Flex
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={{ base: '24px', md: '32px' }}
          textAlign="center"
          maxW="519px"
          w="full"
        >
          <Image
            src={illustration}
            alt={`${errorCode} Error Illustration`}
            w={{ base: 'xs', md: 'sm' }}
            objectFit="contain"
            flexShrink={0}
          />

          <Flex
            display="flex"
            flexDirection="column"
            alignItems="center"
            px="24px"
            gap="24px"
            alignSelf="stretch"
          >
            <Flex
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              gap="4px"
              alignSelf="stretch"
            >
              <Text
                textAlign="center"
                fontSize="13px"
                fontWeight="400"
                color="body.dark"
                lineHeight="150%"
              >
                {title}
              </Text>
              <Heading
                textAlign="center"
                fontSize="30px"
                fontWeight="700"
                color="title.dark"
                lineHeight="150%"
              >
                {subtitle}
              </Heading>
            </Flex>
            <Text
              textAlign="center"
              fontSize="14px"
              fontWeight="500"
              color="body.dark"
              lineHeight="150%"
              whiteSpace="pre-wrap"
            >
              {description}
            </Text>

            <Flex direction={{ base: 'column', sm: 'row' }} gap={3}>
              {!hideGoBack && (
                <Button
                  visual="primary"
                  onClick={handleGoBack}
                  w={{ base: 'full', sm: 'fit-content' }}
                >
                  Go Back
                </Button>
              )}
              {secondaryAction && (
                <Button
                  visual="secondary"
                  onClick={secondaryAction.onClick}
                  w={{ base: 'full', sm: 'fit-content' }}
                >
                  {secondaryAction.label}
                </Button>
              )}
              {primaryAction && (
                <Button
                  visual="primary"
                  onClick={handlePrimaryClick}
                  loading={isPrimaryLoading}
                  disabled={isPrimaryLoading}
                  w={{ base: 'full', sm: 'fit-content' }}
                >
                  {primaryAction.label}
                </Button>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </ErrorLayout>
  );
};

ErrorPage.displayName = 'ErrorPage';
